import { useIntl } from 'react-intl';
import core, { View } from '@gin-betting/core';
import dFnsIsTomorrow from 'date-fns/isTomorrow';
import parseISO from 'date-fns/parseISO';
import { createStructuredSelector } from 'reselect';

import { LIVE_SPORTEVENT_STATUSES, SportEventMetaKey } from 'app-constants';
import { useMemoSelector } from 'hooks';
import globalMsgs from 'messages';
import { selectOverviewScores } from 'selectors';
import { getMapNameFromMeta } from 'utils';

const {
    utils: { formatTimeAndDate },
    entities: {
        selectors: { selectMatchMeta },
    },
    viewport: {
        selectors: { selectIsSmall },
    },
} = core;

interface Input {
    sportEvent: View.Match;
    withDate: boolean;
}

interface Output {
    text: string | null;
    subText: string | null;
}

const OVER_ROUNDS = 6 as const;
const BASE_TOTAL_ROUNDS = 30 as const;

export default function useFixtureDataCS({
    sportEvent,
    withDate,
}: Input): Output {
    const { formatMessage } = useIntl();
    const {
        meta,
        isSmall,
        info: { currentMatchPart },
    } = useMemoSelector(makeSelectProps(), {
        matchId: sportEvent.id,
    });

    if (!withDate) {
        return {
            text: null,
            subText: null,
        };
    }

    const { startTime, status } = sportEvent;
    const isLive = LIVE_SPORTEVENT_STATUSES.includes(status);

    if (isLive) {
        const bo = meta.find(item => item.name === SportEventMetaKey.Bo);

        const boName = bo?.name || '';
        const boValue = bo?.value || '';

        const nameMap = getMapNameFromMeta({
            map: currentMatchPart?.text,
            format: 'long',
        });
        const shotNameMap = getMapNameFromMeta({
            map: currentMatchPart?.text,
            format: 'short',
        });

        const boValueWithColon = boValue ? `${boValue}: ` : '';
        const currentRound =
            meta.find(item => item.name === SportEventMetaKey.Round)?.value ||
            '';

        return {
            text: `${boName.toUpperCase()}${boValueWithColon} ${
                isSmall ? shotNameMap : nameMap
            }`,
            subText:
                currentRound &&
                `R: ${currentRound}/${getRounds(
                    currentRound,
                    BASE_TOTAL_ROUNDS
                )}`,
        };
    }

    const { date, time, isToday } = formatTimeAndDate(startTime, {
        // => 01.10 / Oct 01
        // dateFormat: isSmall ? 'dd.MM' : 'MMM dd',
        dateFormat: 'MMM dd',
    });

    const isTomorrow = dFnsIsTomorrow(parseISO(startTime));

    return {
        text: time,
        subText: isToday
            ? formatMessage(globalMsgs.todayText)
            : isTomorrow
            ? formatMessage(globalMsgs.tomorrowText)
            : date,
    };
}

const makeSelectProps = () =>
    createStructuredSelector({
        meta: selectMatchMeta,
        isSmall: selectIsSmall,
        info: selectOverviewScores,
    });

const getRounds = (nowRound: string, totalRounds: number): number => {
    if (totalRounds >= Number(nowRound)) return totalRounds;

    const nextTotalRounds = totalRounds + OVER_ROUNDS;

    return getRounds(nowRound, nextTotalRounds);
};
